/* L'espace d'une usine.
 *
 * Une coquille à part, et c'est voulu : rien de la navigation d'Omnix ne doit
 * apparaître ici, pas même grisé. Quatre pages — sa fiche, ses références, ses
 * documents, ses commandes — et rien d'autre à atteindre.
 *
 * La base dit déjà non à tout le reste ; cet écran ne fait que ne pas le
 * proposer. Les deux vont ensemble, et c'est le second qui est le confort.
 */

import { useMemo, useState } from "react";
import {
  useMesCommandes, useMesLignesCommande, useMesProduits, useMonUsine, usePrixUsine,
  usePropositions, useContactsUsine, useDocumentsUsine,
} from "@/lib/donnees";
import { useSession } from "@/lib/session";
import { Capsule, Empty, Kpi, Kpis, Onglets, Section, Tag } from "@/ui/primitives";
import { Icon } from "@/ui/Icon";
import { Identite } from "@/ui/Fiche";
import type { GroupeDef } from "@/ui/Fiche";
import { Table, dansUnChamp } from "@/ui/Table";
import { useToast } from "@/ui/Toast";
import { dateFr, entier, euroPrecis, poids } from "@/lib/format";
import { INCOTERMS, LANGUES, PAIEMENTS, UNITES } from "@/domain/fiches";
import { etapeDe } from "@/domain/ventes";
import { enVigueur } from "@/domain/prix";
import { nomNature } from "@/domain/documents-usine";
import { EditeurContacts } from "@/features/contacts/EditeurContacts";
import { DepotRefuse, deposerDocument, lienDocument, retirerDocument } from "@/features/usines/actions";
import { majIdentite, majProduit, proposerPrix } from "./actions";
import type { ProduitUsine, Usine } from "@/types/metier";

type Page = "fiche" | "produits" | "documents" | "commandes";

/* Ce qu'une usine renseigne elle-même. `nom`, `code`, `catégorie` et la note
   interne d'Omnix n'y sont pas : la base les refuse, l'écran ne les propose
   pas non plus. */
const GROUPES_MON_USINE: readonly GroupeDef<Usine>[] = [
  {
    titre: "Coordonnées",
    note: "ce qui sert à vous joindre et à vous facturer",
    champs: [
      { cle: "adresse", nom: "Adresse", large: true },
      { cle: "code_postal", nom: "Code postal", type: "mono" },
      { cle: "ville", nom: "Ville" },
      { cle: "pays", nom: "Pays" },
      { cle: "telephone", nom: "Téléphone", type: "tel" },
      { cle: "email", nom: "Courriel", type: "mail" },
      { cle: "site", nom: "Site", type: "url" },
      { cle: "langue", nom: "Langue d'échange", type: "select", options: LANGUES },
    ],
  },
  {
    titre: "Administratif",
    champs: [
      { cle: "registre", nom: "Registre du commerce", type: "mono" },
      { cle: "tva", nom: "N° de TVA", type: "mono" },
    ],
  },
  {
    titre: "Commerce",
    note: "ce sur quoi nous nous engageons auprès du client",
    champs: [
      { cle: "incoterm", nom: "Incoterm habituel", type: "select", options: INCOTERMS },
      { cle: "conditions_paiement", nom: "Conditions de paiement", type: "select",
        options: PAIEMENTS },
      { cle: "delai_production", nom: "Délai de production",
        aide: "Tel que vous le diriez : « 3 à 4 semaines »." },
    ],
  },
  {
    titre: "Présentation",
    note: "ce que vous diriez de vous",
    champs: [{ cle: "presentation", nom: "Votre maison", type: "zone" }],
  },
];

export function EspaceUsine() {
  const { profil, deconnexion } = useSession();
  const toast = useToast();
  const usineId = profil?.usine_id ?? undefined;

  const { donnees: usine, chargement, erreur, recharger } = useMonUsine(usineId);
  const { donnees: produits, recharger: rechargerProduits } = useMesProduits();
  const { donnees: commandes } = useMesCommandes();
  const { donnees: lignes } = useMesLignesCommande();
  const { donnees: prix } = usePrixUsine();
  const { donnees: propositions, recharger: rechargerPropositions } = usePropositions();
  const { donnees: contacts, recharger: rechargerContacts } = useContactsUsine(usineId);
  const { donnees: documents, chargement: chargeDocs, recharger: rechargerDocs } =
    useDocumentsUsine(usineId);

  const [page, setPage] = useState<Page>("fiche");
  const [theme, setTheme] = useState<string>(
    () => document.documentElement.getAttribute("data-theme") ?? "light",
  );

  const prixDe = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of enVigueur(prix)) m.set(p.produit_id, Number(p.prix));
    return m;
  }, [prix]);

  const enAttente = useMemo(
    () => propositions.filter((p) => p.etat === "en_attente"),
    [propositions],
  );
  const attendue = useMemo(
    () => new Map(enAttente.map((p) => [p.produit_id, p] as const)),
    [enAttente],
  );

  const parCommande = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of lignes) m.set(l.vente_id, (m.get(l.vente_id) ?? 0) + 1);
    return m;
  }, [lignes]);

  const aFaire = useMemo(
    () => produits.filter((p) => !p.code_douane || !p.dluo || p.poids_net == null).length,
    [produits],
  );

  function basculerTheme() {
    const n = theme === "dark" ? "light" : "dark";
    setTheme(n);
    document.documentElement.setAttribute("data-theme", n);
    try {
      window.localStorage.setItem("omnix.desk.theme", n);
    } catch {
      /* stockage refusé : le choix vaut pour la session */
    }
  }

  const pages: { cle: Page; nom: string; n?: number }[] = [
    { cle: "fiche", nom: "Ma fiche" },
    { cle: "produits", nom: "Mes références", n: produits.length },
    { cle: "documents", nom: "Mes documents", n: documents.length },
    { cle: "commandes", nom: "Mes commandes", n: commandes.length },
  ];

  return (
    <div className="shell shell-nu">
      <div className="main">
        <header className="topbar">
          <div className="topbar-marque">
            <Capsule taille={13} />
          </div>
          <h1 className="view-title">{usine?.nom ?? "Espace fournisseur"}</h1>
          <div className="topbar-right">
            <button className="icon-btn" aria-label="Changer de thème" onClick={basculerTheme}>
              <Icon n={theme === "dark" ? "soleil" : "lune"} cls="" />
            </button>
            <button
              className="icon-btn"
              aria-label="Se déconnecter"
              title="Se déconnecter"
              onClick={() => void deconnexion()}
            >
              <Icon n="sortir" cls="" />
            </button>
          </div>
        </header>

        <main className="content">
          <div className="pad">
            {erreur ? <div className="banner">{erreur}</div> : null}
            {chargement ? <div className="chargement">Chargement…</div> : null}

            {!chargement && !usine ? (
              <Empty
                titre="Fiche introuvable"
                detail="Ce compte n'est rattaché à aucune usine. Prévenez votre interlocuteur chez Omnix."
              />
            ) : null}

            {usine ? (
              <>
                <div className="fiche-tete">
                  <div className="fiche-t">
                    <div className="section-t">{usine.nom}</div>
                    <div className="section-n">
                      {[usine.ville, usine.pays].filter(Boolean).join(" · ") ||
                        "Complétez vos coordonnées ci-dessous"}
                    </div>
                  </div>
                  <div className="fiche-a fiche-a-centre">
                    <Tag ton="accent" dot>espace fournisseur</Tag>
                  </div>
                </div>

                <div className="fiche-parcours">
                  <Onglets items={pages} actif={page} onChange={setPage} />
                </div>

                {page === "fiche" ? (
                  <>
                    <Kpis>
                      <Kpi k="Vos références" v={entier(produits.length)}
                           n={aFaire ? `${aFaire} à compléter` : "toutes renseignées"} />
                      <Kpi k="Commandes en cours" v={entier(
                        commandes.filter((c) => c.etape !== "paye" && c.etape !== "facture").length,
                      )} n={`${commandes.length} au total`} />
                      <Kpi k="Prix en attente" v={entier(enAttente.length)}
                           n={enAttente.length ? "chez Omnix" : "rien à trancher"} />
                    </Kpis>

                    <Identite
                      valeur={usine}
                      groupes={GROUPES_MON_USINE}
                      modifiable
                      enregistrer={async (champs) => {
                        await majIdentite(champs);
                        recharger();
                        toast("Fiche enregistrée.");
                      }}
                    />

                    <EditeurContacts
                      table="contacts_usine"
                      colonne="usine_id"
                      parent={usine.id}
                      liste={contacts}
                      modifiable
                      recharger={rechargerContacts}
                    />
                  </>
                ) : null}

                {page === "produits" ? (
                  <MesReferences
                    produits={produits}
                    prixDe={prixDe}
                    attendue={attendue}
                    onChange={() => {
                      rechargerProduits();
                      rechargerPropositions();
                    }}
                  />
                ) : null}

                {page === "documents" ? (
                  <MesDocuments
                    usineId={usine.id}
                    documents={documents}
                    produits={produits}
                    chargement={chargeDocs}
                    onChange={rechargerDocs}
                  />
                ) : null}

                {page === "commandes" ? (
                  <MesCommandes commandes={commandes} lignes={lignes} parCommande={parCommande} />
                ) : null}
              </>
            ) : null}
          </div>
        </main>
      </div>
    </div>
  );
}

/* ───────────────────────────── les références ──────────────────────────── */

function MesReferences({
  produits, prixDe, attendue, onChange,
}: {
  produits: ProduitUsine[];
  prixDe: ReadonlyMap<string, number>;
  attendue: ReadonlyMap<string, { reference: string; prix: number }>;
  onChange: () => void;
}) {
  const [ouvert, setOuvert] = useState<string | null>(null);
  const courant = produits.find((p) => p.id === ouvert) ?? null;

  return (
    <>
      <Section
        titre="Vos références"
        note="ce que nous vendons de chez vous — complétez ce que vous seuls savez"
      >
        {produits.length ? (
          <Table
            lignes={produits}
            cle={(p) => p.id}
            chercher="Référence ou désignation"
            onClic={(p, ev) => {
              if (!dansUnChamp(ev)) setOuvert(p.id);
            }}
            colonnes={[
              { cle: "ref", titre: "Référence", type: "ref", filtre: "texte",
                sous: (p) =>
                  p.code_douane && p.dluo && p.poids_net != null
                    ? p.nom
                    : `${p.nom} · fiche à compléter` },
              { cle: "nom", titre: "Désignation", classe: "name", mobile: false, filtre: "texte" },
              { cle: "conditionnement", titre: "Cond.", type: "ref", mobile: false },
              /* Le prix seul dans sa colonne : l'annonce en attente tient sur
                 la seconde ligne du téléphone, et la table reste à l'écran. */
              { cle: "prix", titre: "Votre prix", type: "euro",
                nombre: (p) => prixDe.get(p.id) ?? 0,
                sous: (p) => {
                  const att = attendue.get(p.id);
                  return att ? `${euroPrecis(att.prix)} en attente` : "";
                },
                rendu: (p) => {
                  const v = prixDe.get(p.id);
                  return <span className="mono">{v == null ? "—" : euroPrecis(v)}</span>;
                } },
              { cle: "attente", titre: "Annoncé", type: "euro", mobile: false,
                nombre: (p) => Number(attendue.get(p.id)?.prix ?? 0),
                rendu: (p) => {
                  const att = attendue.get(p.id);
                  return att
                    ? <Tag ton="warn">{euroPrecis(att.prix)}</Tag>
                    : <span className="no">—</span>;
                } },
              { cle: "complet", titre: "Fiche technique", court: "Fiche", mobile: false,
                texte: (p) => (p.code_douane && p.dluo && p.poids_net != null ? "complète" : "à compléter"),
                rendu: (p) =>
                  p.code_douane && p.dluo && p.poids_net != null
                    ? <Tag ton="ok" dot>complète</Tag>
                    : <Tag ton="warn" dot>à compléter</Tag> },
            ]}
          />
        ) : (
          <Empty
            titre="Aucune référence"
            detail="Les références sont créées par Omnix. Prévenez votre interlocuteur si l'une manque."
          />
        )}
      </Section>

      {courant ? (
        <FeuilleReference
          produit={courant}
          prixActuel={prixDe.get(courant.id) ?? null}
          onFerme={() => setOuvert(null)}
          onChange={onChange}
        />
      ) : null}
    </>
  );
}

const GROUPES_MA_REFERENCE: readonly GroupeDef<ProduitUsine>[] = [
  {
    titre: "Conditionnement",
    champs: [
      { cle: "ref", nom: "Référence", type: "mono", fige: true },
      { cle: "nom", nom: "Désignation", large: true, fige: true },
      { cle: "conditionnement", nom: "Conditionnement", large: true },
      { cle: "unite", nom: "Unité de vente", type: "select", options: UNITES },
      { cle: "poids_net", nom: "Poids net", type: "nombre", unite: "kg" },
      { cle: "poids_brut", nom: "Poids brut", type: "nombre", unite: "kg" },
      { cle: "par_carton", nom: "Unités par carton", type: "nombre" },
      { cle: "par_palette", nom: "Cartons par palette", type: "nombre" },
    ],
  },
  {
    titre: "Qualité et douane",
    note: "ce que nos clients réclament avant de commander",
    champs: [
      { cle: "code_douane", nom: "Code douanier", type: "mono" },
      { cle: "origine", nom: "Origine" },
      { cle: "dluo", nom: "Durée de vie", aide: "« 24 mois », telle que vous la donnez." },
      { cle: "ean", nom: "Code-barres (EAN)", type: "mono" },
      { cle: "allergenes", nom: "Allergènes", large: true },
      { cle: "ingredients", nom: "Ingrédients", type: "zone" },
    ],
  },
];

function FeuilleReference({
  produit, prixActuel, onFerme, onChange,
}: {
  produit: ProduitUsine;
  prixActuel: number | null;
  onFerme: () => void;
  onChange: () => void;
}) {
  const toast = useToast();
  const [prix, setPrix] = useState("");
  const [date, setDate] = useState("");
  const [motif, setMotif] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const annoncer = async () => {
    const v = Number(prix.replace(",", "."));
    if (!Number.isFinite(v) || v <= 0) {
      setErreur("Indiquez un prix.");
      return;
    }
    setOccupe(true);
    setErreur(null);
    try {
      await proposerPrix(produit.id, v, date || null, motif || null);
      setPrix("");
      setDate("");
      setMotif("");
      onChange();
      toast("Prix annoncé. Omnix le validera.");
    } catch (e) {
      setErreur(e instanceof Error ? e.message : "Annonce refusée.");
    } finally {
      setOccupe(false);
    }
  };

  return (
    <>
      <div className="fiche-tete">
        <div className="fiche-t">
          <div className="section-t">{produit.nom}</div>
          <div className="section-n mono">{produit.ref}</div>
        </div>
        <div className="fiche-a fiche-a-centre">
          <button type="button" className="btn" onClick={onFerme}>
            <Icon n="retour" cls="" />
            Toutes mes références
          </button>
        </div>
      </div>

      <Identite
        valeur={produit}
        groupes={GROUPES_MA_REFERENCE}
        modifiable
        enregistrer={async (champs) => {
          await majProduit(produit.id, champs);
          onChange();
          toast("Fiche technique enregistrée.");
        }}
      />

      <Section titre="Votre prix" note="il ne change qu'une fois validé par Omnix">
        <div className="card card-pad">
          {erreur ? <div className="banner">{erreur}</div> : null}
          <dl className="kv">
            <dt>Prix en vigueur</dt>
            <dd className="mono">{prixActuel == null ? "aucun" : euroPrecis(prixActuel)}</dd>
          </dl>
          <div className="fi-form kv-haut">
            <div className="champ">
              <label htmlFor="pu-prix">Nouveau prix</label>
              <input
                id="pu-prix"
                type="number"
                step="0.0001"
                value={prix}
                onChange={(e) => setPrix(e.target.value)}
                placeholder="1,2100"
              />
            </div>
            <div className="champ">
              <label htmlFor="pu-date">Applicable à partir du</label>
              <input id="pu-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="champ fi-large">
              <label htmlFor="pu-motif">Motif</label>
              <input
                id="pu-motif"
                type="text"
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                placeholder="Hausse de la matière première"
              />
            </div>
          </div>
          <div className="jr-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={occupe || !prix}
              onClick={() => void annoncer()}
            >
              <Icon n="envoyer" cls="" />
              {occupe ? "Envoi…" : "Annoncer ce prix"}
            </button>
          </div>
        </div>
      </Section>
    </>
  );
}

/* ───────────────────────────── les documents ───────────────────────────── */

function MesDocuments({
  usineId, documents, produits, chargement, onChange,
}: {
  usineId: string;
  documents: { id: string; titre: string; nom: string; nature: string; langue: string | null;
               taille: number; cree_le: string; chemin: string; produit_id: string | null }[];
  produits: ProduitUsine[];
  chargement: boolean;
  onChange: () => void;
}) {
  const toast = useToast();
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const refDe = useMemo(() => new Map(produits.map((p) => [p.id, p.ref] as const)), [produits]);

  const deposer = async (liste: FileList | null) => {
    if (!liste?.length) return;
    setOccupe(true);
    setErreur(null);
    try {
      for (const f of Array.from(liste)) await deposerDocument(usineId, f);
      onChange();
      toast(liste.length > 1 ? `${liste.length} documents déposés.` : "Document déposé.");
    } catch (e) {
      setErreur(
        e instanceof DepotRefuse ? e.message
          : e instanceof Error ? e.message : "Dépôt refusé.",
      );
    } finally {
      setOccupe(false);
    }
  };

  return (
    <Section
      titre="Vos documents"
      note="présentations, catalogues, fiches techniques"
      droite={
        <label className="btn btn-primary">
          <Icon n="importer" cls="" />
          {occupe ? "Dépôt…" : "Déposer"}
          <input
            type="file"
            multiple
            className="depot-champ"
            disabled={occupe}
            onChange={(e) => {
              void deposer(e.target.files);
              e.target.value = "";
            }}
          />
        </label>
      }
    >
      {erreur ? <div className="banner">{erreur}</div> : null}
      {chargement ? (
        <div className="chargement">Chargement…</div>
      ) : documents.length ? (
        <Table
          lignes={documents}
          cle={(d) => d.id}
          onClic={(d, ev) => {
            if (dansUnChamp(ev)) return;
            void lienDocument(d.chemin).then((u) => {
              if (u) window.open(u, "_blank", "noopener");
            });
          }}
          colonnes={[
            { cle: "titre", titre: "Document", type: "ref", filtre: "texte", sous: (d) => d.nom },
            { cle: "nature", titre: "Nature", filtre: "select",
              texte: (d) => nomNature(d.nature as never),
              rendu: (d) => <Tag>{nomNature(d.nature as never)}</Tag> },
            { cle: "produit_id", titre: "Référence", mobile: false,
              texte: (d) => (d.produit_id ? (refDe.get(d.produit_id) ?? "—") : "—") },
            { cle: "taille", titre: "Taille", mobile: false, texte: (d) => poids(Number(d.taille)) },
            { cle: "cree_le", titre: "Déposé", type: "date", mobile: false },
            { cle: "retirer", titre: "", court: "", mobile: false,
              rendu: (d) => (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    void retirerDocument(d).then(() => {
                      onChange();
                      toast("Document retiré.");
                    });
                  }}
                >
                  Retirer
                </button>
              ) },
          ]}
        />
      ) : (
        <Empty
          titre="Aucun document"
          detail="Déposez votre présentation, votre catalogue et vos fiches techniques : nos agents s'en servent pour vendre."
        />
      )}
    </Section>
  );
}

/* ───────────────────────────── les commandes ───────────────────────────── */

function MesCommandes({
  commandes, lignes, parCommande,
}: {
  commandes: import("@/types/metier").CommandeUsine[];
  lignes: import("@/types/metier").LigneCommandeUsine[];
  parCommande: ReadonlyMap<string, number>;
}) {
  const [ouverte, setOuverte] = useState<string | null>(null);
  const commande = commandes.find((c) => c.id === ouverte) ?? null;
  const siennes = useMemo(
    () => lignes.filter((l) => l.vente_id === ouverte),
    [lignes, ouverte],
  );

  if (commande) {
    return (
      <>
        <div className="fiche-tete">
          <div className="fiche-t">
            <div className="section-t">{commande.reference}</div>
            <div className="section-n">
              {commande.client ?? "—"}
              {commande.client_ville ? ` · ${commande.client_ville}` : ""}
              {commande.client_pays ? `, ${commande.client_pays}` : ""}
            </div>
          </div>
          <div className="fiche-a fiche-a-centre">
            <Tag ton={etapeDe(commande.etape).ton} dot>{etapeDe(commande.etape).nom}</Tag>
            <button type="button" className="btn" onClick={() => setOuverte(null)}>
              <Icon n="retour" cls="" />
              Toutes mes commandes
            </button>
          </div>
        </div>

        <Section titre="À produire" note={`${siennes.length} lignes`}>
          <Table
            lignes={siennes}
            cle={(l) => l.id}
            colonnes={[
              { cle: "ref", titre: "Référence", type: "ref", sous: (l) => l.nom },
              { cle: "nom", titre: "Désignation", classe: "name", mobile: false },
              { cle: "conditionnement", titre: "Cond.", type: "ref", mobile: false },
              { cle: "qte", titre: "Quantité", type: "num", total: true,
                nombre: (l) => Number(l.qte) },
            ]}
          />
        </Section>

        <Section titre="Le suivi">
          <div className="card card-pad">
            <dl className="kv">
              <dt>Notre commande</dt>
              <dd className="mono">
                {commande.bdc_usine
                  ? `${commande.bdc_usine.numero} du ${dateFr(commande.bdc_usine.date)}`
                  : "—"}
              </dd>
              <dt>Expédition</dt>
              <dd className="mono">
                {commande.expedition
                  ? `${dateFr(commande.expedition.date)}${
                      commande.expedition.transporteur ? ` · ${commande.expedition.transporteur}` : ""}`
                  : "à venir"}
              </dd>
              <dt>Livraison</dt>
              <dd className="mono">
                {commande.livraison ? dateFr(commande.livraison.date) : "à venir"}
              </dd>
              <dt>Réserves à la livraison</dt>
              <dd>{commande.livraison?.reserves || "aucune"}</dd>
            </dl>
          </div>
        </Section>
      </>
    );
  }

  return (
    <Section titre="Vos commandes" note={`${commandes.length} documents`}>
      {commandes.length ? (
        <Table
          lignes={commandes}
          cle={(c) => c.id}
          chercher="Document ou client"
          onClic={(c, ev) => {
            if (!dansUnChamp(ev)) setOuverte(c.id);
          }}
          colonnes={[
            { cle: "reference", titre: "Commande", type: "ref", filtre: "texte",
              sous: (c) => c.client ?? "—" },
            { cle: "client", titre: "Destinataire", filtre: "texte", mobile: false,
              texte: (c) => c.client ?? "—" },
            { cle: "lignes", titre: "Lignes", type: "num", mobile: false,
              nombre: (c) => parCommande.get(c.id) ?? 0 },
            { cle: "etape", titre: "Étape", filtre: "select",
              texte: (c) => etapeDe(c.etape).nom,
              rendu: (c) => <Tag ton={etapeDe(c.etape).ton} dot>{etapeDe(c.etape).court}</Tag> },
            { cle: "date_document", titre: "Date", type: "date", mobile: false,
              rendu: (c) => <span className="mono">{dateFr(c.date_document)}</span> },
          ]}
        />
      ) : (
        <Empty
          titre="Aucune commande"
          detail="Les commandes passées chez vous apparaîtront ici dès qu'elles sont engagées."
        />
      )}
    </Section>
  );
}
